import {
  Box,
  HStack,
  Switch,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import Decimal from "decimal.js";
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { EquipmentExtType, PagePermissions } from "../../contexts/types";
import { formatNumber } from "../../utils/utils";
import { CustomNumberInput } from "./CustomNumberInput";
import { ConditionTable } from "./SimulationNewProduct";

/** 設備入力用ヘッダスタイル */
const equipmentHeaderStyle = {
  fontSize: "16px",
};

/** 設備入力用ボディスタイル */
const equipmentBodyStyle = {
  padding: "7px",
};

/**
 * 新製品製造シミュレーションの「設備・人員構成変更」セクションを表示するコンポーネントのProps
 */
interface Props {
  /** 設備情報一覧 */
  equipmentAmounts: EquipmentExtType[];
  /** GoogleSpread連携時の無効値エラー情報 */
  esSyncError: { invalidValueErrors: string[] };
  /** 条件テーブル（工場為替情報等） */
  conditionTable: ConditionTable | undefined;
  /** 画面権限情報 */
  pagePermissions: PagePermissions | undefined;
  /**
   * 設備情報の任意プロパティを変更する
   * @param equipmentId 設備ID
   * @param propertyName 更新対象プロパティ名
   * @param value 更新値
   */
  changeEquipmentAmounts: (equipmentId: string, propertyName: keyof EquipmentExtType, value: any) => void;
  /**
   * 設備数量変更（金額も再計算）
   * @param equipmentId 設備ID
   * @param newQuantity 新しい数量
   */
  changeEquipmentQuantity: (equipmentId: string, newQuantity: number) => void;
  /** 合計設備金額 */
  sumEquipmentAmount: Decimal;
  /** 合計設備金額（選択分） */
  sumSelectedEquipmentAmount: Decimal;
  /** 増減率（設備投資金額） */
  rateOfChangeAmount: number;
}

/**
 * 新製品製造シミュレーションの「設備・人員構成変更」セクションを表示するコンポーネント
 * SimulationNewProduct.tsx から切り出した対応B（コンポーネント分割 + React.memo化）
 */
const EquipmentPersonnelChangeSection: React.FC<Props> = memo((props) => {
  const {
    equipmentAmounts,
    esSyncError,
    conditionTable,
    pagePermissions,
    changeEquipmentAmounts,
    changeEquipmentQuantity,
    sumEquipmentAmount,
    sumSelectedEquipmentAmount,
    rateOfChangeAmount,
  } = props;

  const { t } = useTranslation();

  // console.info(`★レンダリング - 設備・人員構成変更`); // 再描画確認用

  return (
    <Box mt={6} p={4} border="1px solid #ccc" borderRadius="md" shadow="sm">
      <Text mb={4} fontSize="lg" fontWeight="bold">{t('features.InvestmentScreening.SimulationNewProduct.heading.equipmentPersonnelChange')}</Text>
      {/* 無効値エラーメッセージ */}
      {esSyncError.invalidValueErrors.length > 0 && (
        <Box mb={2}>
          {esSyncError.invalidValueErrors.map((error, index) => (
            <Text key={index} color="orange.500" fontSize="sm">{error}</Text>
          ))}
        </Box>
      )}
      <Text mt={2} as="span">{t('features.InvestmentScreening.SimulationNewProduct.label.equipmentInvestment')}</Text>
      <Text as="span"> unit:{conditionTable?.factoryExchange?.currencyName!}</Text>
      <TableContainer borderTop="1px solid" borderBottom="1px solid" borderColor="gray.200" maxHeight="400px" w="100%" overflowX="auto" overflowY="auto" mt={1}>
        <Table className="mm-ceas-table" cellSpacing={0} width="1105px">
          <colgroup>
            <col width="95px" />
            <col width="*" />
            <col width="185px" />
            <col width="120px" />
            <col width="250px" />
            <col width="110px" />
            <col width="110px" />
          </colgroup>
          <Thead position="sticky" top={0} bg="white" zIndex="10" >
            <Tr>
              <Th textAlign="center" style={equipmentHeaderStyle} padding={2}>
                {t('features.InvestmentScreening.SimulationNewProduct.tableHeader.enableDisable')}
              </Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.equipmentName')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.unitPrice')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.quantity')}</Th>
              <Th style={equipmentHeaderStyle}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.amount')}</Th>
              <Th style={equipmentHeaderStyle} padding={2}><Text whiteSpace="normal" overflowWrap="break-word" fontSize={12}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.requiredPersonnel')}</Text></Th>
              <Th style={equipmentHeaderStyle} padding={2}><Text whiteSpace="normal" overflowWrap="break-word" fontSize={12}>{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.personnelWithoutEquipment')}</Text></Th>
            </Tr>
          </Thead>
          <Tbody>
            {equipmentAmounts.map((e: EquipmentExtType) => (
              <Tr key={e.equipmentId} bgColor={e.selected ? "transparent" : "#eee"} >
                <Td textAlign="center" style={equipmentBodyStyle}>
                  <Switch isChecked={e.selected} onChange={(event) => changeEquipmentAmounts(e.equipmentId!, "selected", event.target.checked)} isDisabled={!pagePermissions?.isEditable} />
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content">
                    {e.equipmentName}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatNumber(e.unitPrice!, "WithDecimal", 4)}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Box width="fit-content" ml="auto">
                    <CustomNumberInput
                      value={e.changeInQuantity ?? null}
                      onChange={(value) => {
                        changeEquipmentQuantity(e.equipmentId!, value ?? 0);
                      }}
                      w="80px"
                      size="sm"
                      min={0}
                      max={99999}
                      isDisabled={!e.selected}
                      decimalPlaces={0}
                      bg="white"
                    />
                  </Box>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatNumber(new Decimal(e.amount || 0), conditionTable?.factoryExchange?.currencyName!)}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatNumber(e.numberOfPeopleRequiredForEquipment || 0, "")}
                  </Text>
                </Td>
                <Td style={equipmentBodyStyle}>
                  <Text width="fit-content" ml="auto">
                    {formatNumber(e.numberOfPeopleRequiredForWithoutEquipment || 0, "")}
                  </Text>
                </Td>
              </Tr>
            ))}
          </Tbody>
          <Tfoot
            sx={{
              position: 'sticky',
              bottom: '0',
              backgroundColor: 'white',
              zIndex: 1,
            }}
          >
            <Tr >
              <Td colSpan={4} textAlign="right" fontWeight="bold">{t('features.InvestmentScreening.SimulationNewProduct.tableHeader.total')}</Td>
              <Td textAlign="right" style={equipmentBodyStyle}>
                <HStack justifyContent="flex-end">
                  <VStack>
                    <Tag
                      size='sm'
                      borderRadius='full'
                      variant='solid'
                      colorScheme='teal'
                    >{t('features.InvestmentScreening.SimulationNewProduct.label.beforeChange')}</Tag>
                    <Tag
                      size='sm'
                      borderRadius='full'
                      variant='solid'
                      colorScheme='pink'
                    >{t('features.InvestmentScreening.SimulationNewProduct.label.afterChange')}</Tag>
                  </VStack>
                  <VStack align="flex-end">
                    <Text fontWeight="bold">
                      {formatNumber(sumEquipmentAmount, conditionTable?.factoryExchange?.currencyName!)}
                    </Text>
                    <Text fontWeight="bold" color="red">
                      {formatNumber(sumSelectedEquipmentAmount, conditionTable?.factoryExchange?.currencyName!)}
                    </Text>
                  </VStack>
                </HStack>
              </Td>
              <Td colSpan={2} verticalAlign="bottom" padding={1}>
                <Tag
                  fontWeight="bold"
                  borderRadius='full'
                  variant='solid'
                  colorScheme={rateOfChangeAmount === 0 ? 'gray' : rateOfChangeAmount < 0 ? 'blue' : 'red'}
                >
                  {t('features.InvestmentScreening.SimulationNewProduct.label.rateOfChange', { rate: rateOfChangeAmount })}
                </Tag>
              </Td>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
    </Box>
  );
});

EquipmentPersonnelChangeSection.displayName = "EquipmentPersonnelChangeSection";

export default EquipmentPersonnelChangeSection;
